<script lang="ts">
  /**
   * One line of a list on a tab: today's daily, the running event, the
   * tournament, a best score. A coloured mark, a small label over a bold
   * value, and at the end a chip (a countdown) and/or a chevron. The colour
   * belongs to the mark; the row itself is the ground with a hairline, so a
   * list of them stays scannable. TwinTen's `.tt-menu-row` (its `app.css`).
   *
   * - `mark`: the glyph (an `Icon`, 22px). `markBg` is any CSS colour or
   *   gradient (`linear-gradient(150deg, var(--ui-mark-daily-from),
   *   var(--ui-mark-daily-to))`), `markInk` its glyph colour; by default the
   *   accent and the ink on it.
   * - `chip`: a short string between the text and the chevron ("5d 01h").
   * - `trailing`: anything else at the end, instead of the chevron.
   * - With `onclick` it is a button with a chevron; without, a plain row.
   *
   * Test ids: `testid` on the row, `valueTestid` on the value. No defaults.
   */
  import type { Snippet } from 'svelte';

  import { styleProps } from '../styleProps.js';
  import Icon from './Icon.svelte';

  interface Props {
    readonly label: string;
    readonly value: string;
    readonly mark: Snippet;
    readonly markBg?: string | undefined;
    readonly markInk?: string | undefined;
    readonly chip?: string | undefined;
    /** What the chip means, for a screen reader ("Ends in"). */
    readonly chipLabel?: string | undefined;
    readonly trailing?: Snippet | undefined;
    readonly onclick?: (() => void) | undefined;
    readonly disabled?: boolean;
    readonly opensDialog?: boolean;
    /** An accessible name for the whole row, when label + value do not say it. */
    readonly name?: string | undefined;
    readonly testid?: string | undefined;
    readonly valueTestid?: string | undefined;
  }
  const {
    label,
    value,
    mark,
    markBg,
    markInk,
    chip,
    chipLabel,
    trailing,
    onclick,
    disabled = false,
    opensDialog = false,
    name,
    testid,
    valueTestid,
  }: Props = $props();
</script>

{#snippet content()}
  <span
    class="ui-menu-row-mark"
    use:styleProps={{ background: markBg, color: markInk }}
    aria-hidden="true">{@render mark()}</span
  >
  <span class="ui-menu-row-text">
    <span class="ui-menu-row-label">{label}</span>
    <span class="ui-menu-row-value" data-testid={valueTestid}>{value}</span>
  </span>
  {#if chip !== undefined}
    <span class="ui-menu-row-chip">
      {#if chipLabel !== undefined}<span class="ui-visually-hidden">{chipLabel}</span>{/if}{chip}
    </span>
  {/if}
  {#if trailing !== undefined}
    <span class="ui-menu-row-go">{@render trailing()}</span>
  {:else if onclick !== undefined}
    <span class="ui-menu-row-go" aria-hidden="true"><Icon name="chevronRight" size={20} /></span>
  {/if}
{/snippet}

{#if onclick !== undefined}
  <button
    type="button"
    class="ui-menu-row"
    aria-label={name}
    aria-haspopup={opensDialog ? 'dialog' : undefined}
    {disabled}
    data-testid={testid}
    {onclick}
  >
    {@render content()}
  </button>
{:else}
  <div class="ui-menu-row" aria-label={name} data-testid={testid}>{@render content()}</div>
{/if}

<style>
  .ui-menu-row {
    box-sizing: border-box;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    border: 1px solid var(--ui-line);
    border-radius: var(--ui-radius-md);
    background: var(--ui-ground);
    padding: 0.7rem 0.85rem;
    text-align: left;
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  button.ui-menu-row:disabled {
    opacity: 0.55;
  }

  button.ui-menu-row:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  .ui-menu-row-mark {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: var(--ui-radius-sm);
    background: var(--ui-accent);
    color: var(--ui-on-accent);
  }

  .ui-menu-row-text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .ui-menu-row-label {
    font: var(--ui-text-eyebrow);
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--ui-ink-2);
  }

  .ui-menu-row-value {
    font: var(--ui-text-body);
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ui-menu-row-chip {
    flex: 0 0 auto;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-sunken);
    /* Its edge, where the sunken step is the page itself (black, 1.4.0). */
    outline: 1px solid var(--ui-line);
    outline-offset: -1px;
    color: var(--ui-ink);
    font: var(--ui-text-eyebrow);
    font-weight: 700;
    padding: 0.2rem 0.55rem;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .ui-menu-row-go {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    color: var(--ui-ink-2);
  }

  .ui-visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
